import React from "react"
import { Modal, LoadingSpinner, Paragraph, HeadingThree } from "buoy-core"
import {
  Elements,
  injectStripe,
} from "react-stripe-elements"
import styled, { withTheme } from "styled-components"
import { fontSize, colors, responsive } from "buoy-theme"
import { connect } from "react-redux"
import { StripeProvider } from "react-stripe-elements"
import { CreditCardInputForm, ApplePayBtn, SecurityIcons } from "./Payment"
import { STRIPE_API_KEY } from "../../common/constants"
import { collectTip, collectApplePayTip, setTipped, setDisableTipping, 
  setTipErrorMsg } from "../actions"
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup"
import tracker from '../../common/tracker'

// AB testing variables are of the following
// bool: window.large_tip_version 
// bool: window.tip_msg_ad_free
// int: window.tip_popup_time: 10000 || 15000


const DollarCircle = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  border: 2px solid ${colors.primary};
  background-color: ${props => props.selected ? colors.primary : 'transparent'};
  transition: transform 0.2s ease-out, background 0.2s ease-out;
  -webkit-appearance: none;

  &:hover {
    cursor: pointer;
    background-color: ${colors.primary};
    transform: translateY(-2px);
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);

    span {
      color: white;
    }
  }

  span {
    font-size: ${fontSize.bodyLarger};
    color: ${props => props.selected ? 'white' : colors.primary};
  }
`

const DollarOptionsContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  margin-top: 16px;
  margin-bottom: 16px;
`

const DollarOption = ({amount, selectedAmount, onSelect}) => (
  <DollarCircle onClick={ () => onSelect(amount)} selected={selectedAmount===amount}>
    <span selected={selectedAmount===amount}>${amount}</span>
  </DollarCircle>
)

const DollarOptions = ({ amounts, selectedAmount, onDollarSelect }) => (
  <DollarOptionsContainer>
  { amounts && amounts.map( amount => (
      <DollarOption amount={amount} key={amount}
        selectedAmount={selectedAmount} onSelect={onDollarSelect}/>
  ))}
  </DollarOptionsContainer>
)

const PostSubmitMessagingContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  background-color: ${ props => props.loading ? 'rgba(0,0,0,0.95)':'rgba(255, 255, 255, 0.95)' };
  z-index: 800;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.15s ease-in;
  ${responsive("padding", "40px", "40px", "40px", "40px", "24px")};
`

const PostSubmitMessaging = ({header, successMsg, isLoading}) => (
  <PostSubmitMessagingContainer>
    <CSSTransitionGroup
      transitionName="simple-fade"
      transitionEnterTimeout={500}
      transitionLeaveTimeout={300}>
      { isLoading && <LoadingSpinner /> }
    </CSSTransitionGroup>
    <CSSTransitionGroup
      transitionName="simple-fade"
      transitionEnterTimeout={500}
      transitionLeaveTimeout={300}>
      { !isLoading && (
        <div>
          <HeadingThree fontSize="h3" 
            weight="light">
            {header}
          </HeadingThree>
          <Paragraph center>{successMsg}</Paragraph>
        </div>)}
    </CSSTransitionGroup>
  </PostSubmitMessagingContainer>
)

class TipWithCardApplePay extends React.Component {
  state = {
    canMakePayment: false,
    paymentRequest: null,
    selectedAmount: window.large_tip_version? 5:2,
  }

  componentDidMount() {
    // reset tipping so if someone wanted to, they can tip multiple times
    this.reset()
    
    const paymentRequest = this.props.stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        amount: this.state.selectedAmount*100, // unit is cents for usd
        label: "Tip for Buoy"
      }
    });

    paymentRequest.on('token', ({complete, token}) => {
      this.props.collectApplePayTip(token, this.state.selectedAmount*100).then(() => {
        complete('success')
      })
    });

    paymentRequest.canMakePayment().then((result) => {
      this.setState({canMakePayment: result ? true : false, paymentRequest});
    });
  }
  
  selectDollarAmount = amount => {
    this.setState({selectedAmount: amount})
  }

  payTip = () => {
    this.props.collectTip(this.props.stripe, this.state.selectedAmount*100)
  }
  
  reset = () => {
    // reset tipping so if someone wanted to, they can tip multiple times
    this.props.setTipped(false)
    this.props.setDisableTipping(false)
    this.props.setTipErrorMsg("")
    tracker.tipModalShowed()
  }

  render() {
    const header_msg = window.tip_msg_ad_free ? "Keep Buoy ad-free." : "Add a tip."
    const body_msg = window.tip_msg_ad_free ? "If you found your visit helpful, you can support Buoy directly by adding a tip. Your tip helps Buoy stay ad-free by covering costs like our doctors, programmers, and computer servers." : "If you found your visit helpful, you can support Buoy directly by adding a tip. Your tip goes to everything needed for Buoy to work, like our doctors, programmers, and computer servers."
    
    return (
      <Modal
        id="tip_modal"
        heading={header_msg}
        message={body_msg}
        showSubmit={true}
        submitText={"Tip $"+ this.state.selectedAmount+" USD"}
        submitEnabled={!this.props.tippingDisabled}
        showCancel={true}
        submitClick={this.payTip}
        cancelText="No thanks"
        cancelClick={() => this.props.showModal(false)}
        closeOnOutsideClick={true}
      >
        { window.large_tip_version ? (
          <DollarOptions amounts={[2,5,10]} 
            selectedAmount={this.state.selectedAmount}
            onDollarSelect={this.selectDollarAmount} />):(
          <DollarOptions amounts={[1,2,5]}
            selectedAmount={this.state.selectedAmount}
            onDollarSelect={this.selectDollarAmount} />)
        }
        { this.state.canMakePayment && (
          <div>
            <ApplePayBtn paymentRequest={this.state.paymentRequest} /> 
            <Paragraph center color="neutralLight" fontSize="bodySmall" margin="4px 0">or</Paragraph>
          </div>)
        }
        <CreditCardInputForm theme={this.props.theme} 
          canMakePayment={this.state.canMakePayment}
          disabled={this.props.tippingDisabled}
          errorMsg={this.props.tipErrorMsg} />
        <SecurityIcons />
        <CSSTransitionGroup
          transitionName="simple-fade"
          transitionEnterTimeout={500}
          transitionLeaveTimeout={300}>
          { this.props.tippingDisabled && (<PostSubmitMessaging  
            isLoading={!this.props.tipped}
            header="Thank you for the tip." 
            successMsg="Health takes help, and we really appreciate your support." />)}
        </CSSTransitionGroup>
      </Modal>
    )
  }
}

const InjectedTipModal = injectStripe(TipWithCardApplePay)

const StripeTipModal = props => (
  <StripeProvider apiKey={STRIPE_API_KEY}>
    <Elements>
      <InjectedTipModal {...props} />
    </Elements>
  </StripeProvider>
)

const mapStateToProps = state => ({
  tipped: state.payment.tipped,
  tippingDisabled: state.payment.tippingDisabled,
  tipErrorMsg: state.payment.tipErrorMsg,
})

const mapDispatchToProps = dispatch => ({
  collectTip: (stripe, amount) => dispatch(collectTip(stripe, amount)),
  collectApplePayTip: (token, amount) => dispatch(collectApplePayTip(token, amount)),
  setTipped: bool => dispatch(setTipped(bool)),
  setDisableTipping: bool => dispatch(setDisableTipping(bool)),
  setTipErrorMsg: msg => dispatch(setTipErrorMsg(msg))
})

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(StripeTipModal)
)




// WEBPACK FOOTER //
// ./assets/payment/components/TipModal.js